import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, CardTitle, Label, PageHeader } from '@nexply/ui';
import type { BackupImportInput, BackupImportResult } from '@nexply/api-client';
import { api } from '../../lib/api.js';

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

// Die Datei aus dem Dateisystem ist ungeprüft: ältere Backups kennen die neueren
// Listen nicht — darum überall optional zählen.
type BackupFileData = BackupImportInput['data'];

interface Counts {
  members: number;
  matches: number;
  relationships: number;
}

function countData(d: BackupFileData): Counts {
  return {
    members: d.members?.length ?? 0,
    matches: d.matches?.length ?? 0,
    relationships: d.relationships?.length ?? 0,
  };
}

const LABELS: Record<keyof Counts, string> = {
  members: 'Mitglieder',
  matches: 'Matches',
  relationships: 'Beziehungen',
};

export function BackupPage() {
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const [fileData, setFileData] = useState<BackupFileData | null>(null);
  const [fileName, setFileName] = useState<string>('');
  // Version der Datei (fehlt bei Backups vor Version 2) — der Server entscheidet damit,
  // wie er fehlende Felder auffüllt.
  const [fileVersion, setFileVersion] = useState<number | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [result, setResult] = useState<BackupImportResult | null>(null);

  async function handleExport() {
    setExportErr(null);
    setExporting(true);
    try {
      const data = await api.backup.export();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexply-backup-${fmtDate(new Date())}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr((e as Error).message ?? 'Export fehlgeschlagen.');
    } finally {
      setExporting(false);
    }
  }

  async function handleFile(f: File | null) {
    setFileErr(null);
    setResult(null);
    setFileData(null);
    setFileName('');
    setFileVersion(null);
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      const data: BackupFileData = parsed?.data ?? parsed;
      if (!data || !Array.isArray(data.members)) {
        setFileErr('Datei ist kein gültiges Nexply-Backup.');
        return;
      }
      setFileData(data);
      setFileName(f.name);
      setFileVersion(typeof parsed?.version === 'number' ? parsed.version : null);
    } catch (e) {
      setFileErr((e as Error).message ?? 'Datei konnte nicht gelesen werden.');
    }
  }

  const importMut = useMutation({
    mutationFn: async () => {
      if (!fileData) throw new Error('no_data');
      return api.backup.import({ mode, version: fileVersion, data: fileData });
    },
    onSuccess: (res) => setResult(res),
  });

  const counts = fileData ? countData(fileData) : null;

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Backup & Wiederherstellung"
        subtitle="Exportiere alle Daten als JSON-Datei oder importiere ein früheres Backup."
      />

      <Card>
        <CardHeader>
          <CardTitle>Backup exportieren</CardTitle>
        </CardHeader>
        <CardBody className="pt-0 space-y-3">
          <p className="text-sm text-text-dim">
            Lädt ein vollständiges JSON-Backup deiner Daten herunter (Mitglieder, Beziehungen, Matches, Termine, Events,
            Deals inkl. Beteiligten, Partner, Markt, Posts, Pitch).
          </p>
          {exportErr && <div className="text-xs text-danger">{exportErr}</div>}
          <Button onClick={handleExport} disabled={exporting}>
            {exporting ? 'Lädt…' : 'Backup jetzt herunterladen'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Backup importieren</CardTitle>
        </CardHeader>
        <CardBody className="pt-0 space-y-4">
          <div>
            <Label>Backup-Datei (.json)</Label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) => handleFile(e.currentTarget.files?.[0] ?? null)}
              className="block w-full text-sm text-text-dim file:mr-3 file:rounded-md file:border-0 file:bg-raise-2 file:px-3 file:py-1.5 file:text-text hover:file:bg-raise-3"
            />
            {fileErr && <div className="mt-2 text-xs text-danger">{fileErr}</div>}
          </div>

          {fileData && counts && (
            <>
              <div className="rounded-lg border border-line bg-raise p-3">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Vorschau · {fileName}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                  {(Object.keys(LABELS) as (keyof Counts)[]).map((k) => (
                    <div key={k} className="flex justify-between text-text-dim">
                      <span>{LABELS[k]}</span>
                      <span className="text-text tabular-nums">{counts[k]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Import-Modus</Label>
                <div className="space-y-2 mt-1">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      checked={mode === 'merge'}
                      onChange={() => setMode('merge')}
                      className="mt-1 accent-gold"
                    />
                    <span className="text-sm text-text-dim">
                      <span className="text-text font-medium">Zusammenführen</span> – fügt alle Items als neue Einträge hinzu.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      checked={mode === 'replace'}
                      onChange={() => setMode('replace')}
                      className="mt-1 accent-gold"
                    />
                    <span className="text-sm text-text-dim">
                      <span className="text-text font-medium">Ersetzen</span> – löscht alle bestehenden Daten und importiert dann.
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-line">
                {importMut.isError && <span className="text-xs text-danger">Import fehlgeschlagen.</span>}
                <Button
                  variant={mode === 'replace' ? 'danger' : 'primary'}
                  disabled={importMut.isPending}
                  onClick={() => {
                    const ok =
                      mode === 'replace'
                        ? confirm('Alle bestehenden Daten werden gelöscht und durch das Backup ersetzt. Fortfahren?')
                        : confirm('Backup wird mit bestehenden Daten zusammengeführt. Fortfahren?');
                    if (ok) importMut.mutate();
                  }}
                >
                  {importMut.isPending ? 'Importiert…' : 'Importieren'}
                </Button>
              </div>
            </>
          )}

          {result && (
            <div className="rounded-lg border border-success/25 bg-success/10 p-3 text-sm">
              <div className="text-success font-medium mb-1">Import abgeschlossen.</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-text-dim">
                {(Object.keys(LABELS) as (keyof Counts)[]).map((k) => (
                  <div key={k} className="flex justify-between">
                    <span>{LABELS[k]}</span>
                    <span className="text-text tabular-nums">{result.createdCounts[k]}</span>
                  </div>
                ))}
              </div>
              {result.errors.length > 0 && (
                <details className="mt-2 text-xs text-text-muted">
                  <summary className="cursor-pointer">{result.errors.length} Hinweise</summary>
                  <ul className="mt-1 space-y-0.5 pl-3 list-disc">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
